import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Form, InputGroup, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiAccountTieOutline, mdiChevronLeft, mdiEye, mdiEyeOff } from "@mdi/js";
import Select from "react-select";

import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { getUsuariosUsuario, getGetUsuariosUsuarioQueryKey, postUsuariosCadastrar, postUsuariosAtualizar, useGetEmpresasListar } from "../../config/swagger";
import { SelectStyle } from "../../config/select";
import type { CadastrarUsuarioRequest } from "../../model/cadastrarUsuarioRequest";
import type { AtualizarUsuarioRequest } from "../../model/atualizarUsuarioRequest";
import type { CadastrarUsuarioResponseIResponse } from "../../model/cadastrarUsuarioResponseIResponse";
import type { AtualizarUsuarioResponseIResponse } from "../../model/atualizarUsuarioResponseIResponse";

import Layout from "../../components/Layout";

const toastTitle = "Usuário";

type UsuarioFormData = {
	id?: number;
	empresaId?: number;
	nome?: string | null;
	documento?: string | null;
	email?: string | null;
	senha?: string | null;
	telefone?: string | null;
	dataCriacao?: string | null;
	dataAtualizacao?: string | null;
};

export default function UsuarioFormulario() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { handleToast } = useToast();
	const { user } = useAuth();

	const [formStatus, setFormStatus] = useState(id ? false : true);
	const [formSaving, setFormSaving] = useState(false);
	const [senhaExibir, setSenhaExibir] = useState(false);

	const { data, isLoading, isFetching, isRefetching } = useQuery(getGetUsuariosUsuarioQueryKey({ id: Number(id) }), () => fetchData(id!), { enabled: !!id && !formStatus });
	const queryEmpresas = useGetEmpresasListar({}, { query: { enabled: user?.superUser === true } });

	const {
		register,
		handleSubmit,
		reset,
		control,
		formState: { errors },
	} = useForm<UsuarioFormData>({ defaultValues: data?.Data });

	const mutationPost = useMutation(mutatePost, { onSuccess: mutatePostSuccess, onError: mutateError });
	const mutationPut = useMutation(mutatePut, { onSuccess: mutatePutSuccess, onError: mutateError });

	async function fetchData(id: string) {
		const resp = await getUsuariosUsuario({ id: Number(id) });
		reset(resp.Data);
		return resp;
	}

	async function mutatePost(data: CadastrarUsuarioRequest) {
		setFormSaving(true);
		return await postUsuariosCadastrar(data);
	}

	function mutatePostSuccess(resp: CadastrarUsuarioResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		if (resp.Data?.id) {
			navigate("/usuarioFormulario/" + resp.Data.id);
		}
		setFormStatus(false);
		setFormSaving(false);
	}

	async function mutatePut(data: AtualizarUsuarioRequest) {
		setFormSaving(true);
		return await postUsuariosAtualizar(data);
	}

	function mutatePutSuccess(resp: AtualizarUsuarioResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		setFormStatus(false);
		setFormSaving(false);
	}

	function mutateError(resp: AxiosError) {
		handleToast(toastTitle, `Problema na operação: ${resp.message}`, 5000, "danger");
		setFormSaving(false);
	}

	return (
		<Layout>
			<Form
				onSubmit={handleSubmit((values) => {
					if (id) {
						mutationPut.mutate({
							id: Number(id),
							nome: values.nome,
							email: values.email,
							senha: values.senha,
							documento: values.documento,
							telefone: values.telefone,
						});
					} else {
						mutationPost.mutate({
							nome: values.nome,
							email: values.email,
							senha: values.senha,
							documento: values.documento,
							telefone: values.telefone,
						});
					}
				})}
			>
				<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
					<Link to={"/usuarios"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiAccountTieOutline} size={1} className="me-2" /> Formulário de Usuário
					</Link>
					{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex gap-2 float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
						{!formStatus && (
							<Button
								variant="dark"
								className="rounded-3 shadow-sm"
								onClick={() => {
									setFormStatus(true);
								}}
							>
								Editar Informações
							</Button>
						)}
						{formStatus && (
							<>
								<Button
									variant="light"
									className="rounded-3 shadow-sm"
									type="button"
									onClick={() => {
										if (id) {
											setFormStatus(false);
										} else {
											navigate("/usuarios");
										}
									}}
									disabled={formSaving}
								>
									Cancelar
								</Button>
								<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
									{formSaving ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" /> Salvando
										</>
									) : (
										"Salvar Informações"
									)}
								</Button>
							</>
						)}
					</div>
				</h5>

				<Card className="mb-3">
					<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">Informações Básicas</Card.Header>
					<Card.Body>
						{!id && user?.superUser && (
							<Form.Group className="mb-3" controlId="empresaId">
								<Form.Label>Empresa</Form.Label>
								<Controller
									name="empresaId"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<Select
											options={
												queryEmpresas.data?.Data?.map((empresa) => ({
													value: empresa.id,
													label: empresa.nomeFantasia || empresa.razaoSocial,
												})) || []
											}
											value={
												queryEmpresas.data?.Data?.filter((empresa) => empresa.id === field.value).map((empresa) => ({
													value: empresa.id,
													label: empresa.nomeFantasia || empresa.razaoSocial,
												}))[0] || null
											}
											onChange={(selected) => field.onChange(selected?.value)}
											isDisabled={!formStatus || !!id}
											isLoading={queryEmpresas.isLoading}
											styles={SelectStyle}
											placeholder="Selecione a empresa..."
											noOptionsMessage={() => "Nenhuma empresa disponível"}
										/>
									)}
								/>
							</Form.Group>
						)}
						<Form.Group className="mb-3" controlId="nome">
							<Form.Label>Nome</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("nome", { required: true })} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="email">
							<Form.Label>E-mail</Form.Label>
							<Form.Control type="email" placeholder="Informe aqui" {...register("email", { required: true })} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="documento">
							<Form.Label>Documento</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("documento")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="telefone">
							<Form.Label>Telefone</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("telefone")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="senha">
							<Form.Label>Senha</Form.Label>
							<InputGroup hasValidation>
								<Form.Control
									type={senhaExibir ? "text" : "password"}
									placeholder="Informe aqui"
									{...register("senha", {
										required: !id,
										validate: (value) => {
											if (!value) return true;
											return (value as string).length === 0 || (value as string).length >= 8 || "Tamanho mínimo de 8 caracteres.";
										},
									})}
									isInvalid={!!errors.senha}
									disabled={!formStatus}
								/>
								<Button
									variant="light"
									className="border"
									onClick={() => {
										setSenhaExibir(!senhaExibir);
									}}
								>
									<Icon path={senhaExibir ? mdiEye : mdiEyeOff} size={1} />
								</Button>
								<Form.Control.Feedback type="invalid">{errors.senha?.message}</Form.Control.Feedback>
							</InputGroup>
							<Form.Text className="d-block">Caso não queira trocar a senha, manter o campo em branco.</Form.Text>
						</Form.Group>
					</Card.Body>
				</Card>

				<div className="d-flex gap-2 mb-4">
					{!formStatus && (
						<Button
							variant="dark"
							className="rounded-3 shadow-sm"
							onClick={() => {
								setFormStatus(true);
							}}
						>
							Editar Informações
						</Button>
					)}
					{formStatus && (
						<>
							<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
								{formSaving ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" /> Salvando
									</>
								) : (
									"Salvar Informações"
								)}
							</Button>
							<Button
								variant="light"
								className="rounded-3 shadow-sm"
								type="button"
								onClick={() => {
									if (id) {
										setFormStatus(false);
									} else {
										navigate("/usuarios");
									}
								}}
								disabled={formSaving}
							>
								Cancelar
							</Button>
						</>
					)}
				</div>
			</Form>
		</Layout>
	);
}
